/**
 * Main Layout Component
 */

import React from 'react';
import { Calculator, Truck, LayoutDashboard, Store, Globe, Settings, LogOut, User, Scale, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';

export type ModuleType = 'dashboard' | 'marketplaces' | 'priceComparison' | 'shipping' | 'profitability' | 'settings';

interface LayoutProps {
  children: React.ReactNode;
  activeModule: ModuleType;
  onModuleChange: (module: ModuleType) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, activeModule, onModuleChange }) => {
  const { language, setLanguage, t } = useLanguage();
  const { user, role, logout } = useAuth();

  const navItems: { id: ModuleType; icon: typeof Calculator }[] = [
    { id: 'dashboard', icon: LayoutDashboard },
    { id: 'marketplaces', icon: Store },
    { id: 'priceComparison', icon: Scale },
    { id: 'shipping', icon: Truck },
    { id: 'profitability', icon: TrendingUp },
    { id: 'settings', icon: Settings },
  ];

  const getNavLabel = (id: ModuleType): string => {
    switch (id) {
      case 'dashboard': return 'Dashboard';
      case 'marketplaces': return t.modules.marketplaces;
      case 'priceComparison': return language === 'tr' ? 'Fiyat Kıyas' : 'Price Comparison';
      case 'shipping': return t.modules.shipping;
      case 'profitability': return language === 'tr' ? 'Karlılık' : 'Profitability';
      case 'settings': return language === 'tr' ? 'Ayarlar' : 'Settings';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Skip to main content link (visually hidden until focused) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        {language === 'tr' ? 'Ana içeriğe geç' : 'Skip to main content'}
      </a>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo + Language Switcher */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <img src="/pricelab-icon.svg" alt="PriceLab" className="w-10 h-10" />
                <div>
                  <h1 className="text-xl font-bold text-slate-800">PriceLab</h1>
                  <p className="text-xs text-slate-500">Multi-Marketplace Pricing Tool</p>
                </div>
              </div>

              {/* Language Switcher */}
              <button
                onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors border border-slate-200"
                title={language === 'tr' ? 'Switch to English' : 'Türkçe\'ye geç'}
                aria-label={language === 'tr' ? 'Switch to English' : 'Türkçe\'ye geç'}
              >
                <Globe className="w-3.5 h-3.5" aria-hidden="true" />
                {language === 'tr' ? 'EN' : 'TR'}
              </button>
            </div>

            {/* Right side - Navigation + User */}
            <div className="flex items-center gap-4">
              {/* Navigation */}
              <nav className="flex items-center gap-1">
                {navItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => onModuleChange(item.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeModule === item.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {getNavLabel(item.id)}
                  </button>
                ))}
              </nav>

              {/* User Info & Logout */}
              {user && (
                <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                  <div className="flex items-center gap-2">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-indigo-600" />
                      </div>
                    )}
                    <div className="text-right">
                      <div className="text-sm font-medium text-slate-800">{user.name}</div>
                      <div className="text-xs text-slate-500 capitalize">{role}</div>
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Logout"
                    aria-label={language === 'tr' ? 'Çıkış yap' : 'Logout'}
                  >
                    <LogOut className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>
    </div>
  );
};

export default Layout;
